export interface ChangelogSection {
  title: string;
  items: string[];
}

export interface ChangelogRelease {
  version: string;
  title: string;
  released?: string;
  sections: ChangelogSection[];
}

const RELEASE_HEADING = /^## \[([^\]]+)\]\s*(?:—|-)\s*(.+)$/;

/** Normalize line endings and strip maintainer preamble before the first release heading. */
function normalizeMarkdown(markdown: string): string {
  const normalized = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const firstRelease = normalized.search(/^## \[/m);
  if (firstRelease === -1) {
    return normalized.trim();
  }
  return normalized.slice(firstRelease).trim();
}

/** Parse user-facing CHANGELOG.md into structured release entries. */
export function parseChangelog(markdown: string): ChangelogRelease[] {
  const body = normalizeMarkdown(markdown);
  if (!body) {
    return [];
  }

  const releases: ChangelogRelease[] = [];
  const blocks = body.split(/\n(?=## \[)/);

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    if (lines.length === 0) {
      continue;
    }

    const headingMatch = lines[0].match(RELEASE_HEADING);
    if (!headingMatch) {
      continue;
    }

    const [, version, title] = headingMatch;
    let released: string | undefined;
    const sections: ChangelogSection[] = [];
    let currentSection: ChangelogSection | null = null;

    for (let i = 1; i < lines.length; i += 1) {
      const line = lines[i].trim();
      if (!line || line === '---') {
        continue;
      }

      const releasedMatch = line.match(/^Released:\s*(.+)$/i);
      if (releasedMatch) {
        released = releasedMatch[1].trim();
        continue;
      }

      const sectionMatch = line.match(/^###\s+(.+)$/);
      if (sectionMatch) {
        currentSection = { title: sectionMatch[1].trim(), items: [] };
        sections.push(currentSection);
        continue;
      }

      const bulletMatch = line.match(/^-\s+(.+)$/);
      if (bulletMatch && currentSection) {
        currentSection.items.push(bulletMatch[1].trim());
      }
    }

    releases.push({ version, title: title.trim(), released, sections });
  }

  return releases;
}
